import { useMemo, useState } from 'react';
import { GitCompare, ShieldCheck, RotateCcw, AlertTriangle } from 'lucide-react';
import { diffLines, collapseUnchanged } from '../lib/lineDiff.js';
import { formatDateTimeWithZone } from '../lib/format.js';

// CAMPAIGN BEHAVIOR BLOCK — layer 2 of the prompt contract
// (docs/pitch-authority-architecture-2026-07-28.md, step 7).
//
// Background, because it decides everything about this screen: HOW the AI sells —
// opener policy, deflection/two-track, discovery-before-pivot, the document offer —
// used to be 25,660 characters of Python appended late in the prompt, outranking the
// campaign's own pitch. Any session could edit it, it shipped with unrelated work,
// and nobody reviewed it as a pitch change. On 2026-07-28 ~40 appended lines doubled
// turn latency and moved the hard ask to turn two. Nothing failed. No test broke. The
// only detector was Brian's ear, after a live call.
//
// So this editor exists to make that class of change VISIBLE before it reaches a call:
//
//   • a DIFF against the last version validated by ear — not just "you have unsaved
//     changes", but exactly which lines moved;
//   • the LAST-VALIDATED copy retained, so rollback is one click rather than
//     timestamp archaeology across three deploys (which is what it actually took);
//   • marking a version validated is a DELIBERATE act, separate from saving, because
//     it asserts something only a human ear can assert.
//
// Guardrails (layer 3, compliance) are NOT here and never will be. The rule is: if it
// is about how to sell, it belongs in this box; if it is about what we are not
// permitted to do, it belongs in code.

// Advisory, NOT a hard `maxLength`. A textarea's maxLength truncates a paste
// silently — for a string that reaches a live call, losing the tail without a
// word is the wrong failure. Warn instead and let the server be the authority.
// ⚠️ Core's cap must be >= this number, or the page's full-object save 400s on
// EVERY edit — that is the systemPromptOverride 8000 bug, verbatim.
const BEHAVIOR_SOFT_MAX = 40000;

// The panel substitutes this at prompt-assembly time. It looks like leftover
// template junk in the editor, so a tidy-up would quietly delete a live feature's
// hook — hence the explicit warning below rather than trusting it to survive.
const TIP_PLACEHOLDER = '{{TIP_RESEARCH_CLAUSE}}';

function DiffRow({ row }) {
  if (row.type === 'gap') {
    return (
      <div className="px-3 py-1 text-center text-[11px] text-stone-600">
        ⋯ {row.count} unchanged line{row.count === 1 ? '' : 's'}
      </div>
    );
  }
  const tone =
    row.type === 'added'
      ? 'bg-emerald-950/40 text-emerald-200'
      : row.type === 'removed'
        ? 'bg-rose-950/40 text-rose-200'
        : 'text-stone-400';
  const mark = row.type === 'added' ? '+' : row.type === 'removed' ? '−' : ' ';
  return (
    <div className={`flex gap-2 px-3 py-0.5 font-mono text-xs ${tone}`}>
      <span className="select-none opacity-60">{mark}</span>
      <span className="whitespace-pre-wrap break-words">{row.text || ' '}</span>
    </div>
  );
}

export default function CampaignBehaviorEditor({
  value,
  onChange,
  saved,            // the last SAVED block — always available, no core work needed
  validated,        // the last ear-validated copy (string | null), if core has it
  validatedAt,      // ISO timestamp | null
  onValidate,       // () => Promise — marks the SAVED block as validated
  onRestore,        // (text) => void — puts the validated copy back in the editor
  dirty,            // unsaved edits pending
  busy = false,
  supportsValidation = true
}) {
  const [showDiff, setShowDiff] = useState(false);

  const current = value || '';

  // Two baselines, in priority order. The last VALIDATED version answers the
  // better question ("what changed since the call that sounded right?"), but it
  // depends on a column core may not have. The last SAVED version depends on
  // nothing and answers the more frequent question ("what am I about to change
  // with this save?"). Falling back means the diff — the entire point of this
  // screen — works on day one instead of sitting disabled.
  const baseIsValidated = !!validated;
  const base = validated || saved || '';
  const hasBase = !!base;

  const diff = useMemo(() => {
    if (!showDiff || !hasBase) return null;
    const d = diffLines(base, current);
    if (d.tooLarge) return d;
    return { ...d, rows: collapseUnchanged(d.rows) };
  }, [showDiff, hasBase, base, current]);

  const identical = hasBase && base === current;

  // Deleting the placeholder is silent and irreversible from the AI's point of
  // view: the expert-tip promise simply stops being made.
  const droppedTipPlaceholder =
    (saved || '').includes(TIP_PLACEHOLDER) && !current.includes(TIP_PLACEHOLDER);

  const overCap = current.length > BEHAVIOR_SOFT_MAX;

  return (
    <div>
      <label className="field-label">Campaign behavior</label>
      <p className="mt-1 mb-2 text-sm text-stone-400">
        HOW this campaign runs the call — the opener policy, what to do when someone
        deflects, when to move from discovery to the offer, how to offer the document.
        This used to live in code and outrank the pitch; it lives here now, so changing
        it is an edit rather than a deploy.
      </p>

      {/* Null is not "empty" — it means this campaign is still running the built-in
          block. Saying so prevents someone reading a blank box as "no behavior". */}
      {!current && (
        <p className="mb-2 rounded-lg border border-stone-700 bg-stone-900/40 px-3 py-2 text-sm text-stone-300">
          This campaign is running the built-in behavior. Write here to take ownership of
          it for this campaign — until then it follows the platform default.
        </p>
      )}

      <textarea
        className="field-input min-h-[320px] font-mono text-sm leading-relaxed"
        placeholder="How to run the call for this campaign. Not the guardrails — those are enforced separately and always win."
        value={current}
        onChange={(e) => onChange(e.target.value)}
        disabled={busy}
      />

      {droppedTipPlaceholder && (
        <p className="mt-2 flex items-start gap-2 rounded-lg border border-amber-600/40 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            You removed <code className="font-mono">{TIP_PLACEHOLDER}</code>. That is not leftover
            template text — the AI replaces it at call time with the tailored tip it offers the
            person on the phone. Without it that offer stops being made. Put it back unless you
            mean to turn it off.
          </span>
        </p>
      )}

      {overCap && (
        <p className="mt-2 flex items-start gap-2 rounded-lg border border-amber-600/40 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            Longer than {BEHAVIOR_SOFT_MAX.toLocaleString()} characters. Nothing has been cut — but
            the server may refuse it, so trim before saving.
          </span>
        </p>
      )}

      <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn-ghost"
            onClick={() => setShowDiff((s) => !s)}
            disabled={!hasBase}
            title={hasBase ? '' : 'Nothing to compare against yet.'}
          >
            <GitCompare size={14} />{' '}
            {showDiff
              ? 'Hide changes'
              : baseIsValidated
                ? 'Compare to last validated'
                : 'Compare to saved'}
          </button>

          {supportsValidation && (
            <button
              type="button"
              className="btn-ghost"
              onClick={onValidate}
              disabled={busy || dirty || !current}
              title={
                dirty
                  ? 'Save first — you can only validate what is actually running.'
                  : 'Record this as the version confirmed by ear on a real call.'
              }
            >
              <ShieldCheck size={14} /> Mark as validated
            </button>
          )}

          {hasBase && !identical && (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => onRestore(validated)}
              disabled={busy}
            >
              <RotateCcw size={14} /> Restore last validated
            </button>
          )}
        </div>

        <span className={`text-xs tabular-nums ${overCap ? 'text-amber-300' : 'text-stone-500'}`}>
          {current.length.toLocaleString()} / {BEHAVIOR_SOFT_MAX.toLocaleString()} characters
        </span>
      </div>

      <div className="mt-2 text-xs">
        {validatedAt ? (
          identical && !dirty ? (
            <span className="text-emerald-300">
              Running the version validated {formatDateTimeWithZone(validatedAt)}.
            </span>
          ) : (
            <span className="text-amber-300">
              Changed since the version validated {formatDateTimeWithZone(validatedAt)} — this has
              not been heard on a real call yet.
            </span>
          )
        ) : (
          <span className="text-stone-500">
            No validated version recorded yet. Mark one after a call you have listened to, and
            every later change gets measured against it.
          </span>
        )}
      </div>

      {dirty && (
        <p className="mt-2 flex items-start gap-2 text-xs text-amber-300">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          Unsaved. Saving changes what the next call says — this is a pitch change, so ship it on
          its own and listen to a call before marking it validated.
        </p>
      )}

      {showDiff && diff && (
        <div className="mt-3 overflow-hidden rounded-lg border border-stone-800 bg-stone-950/60">
          <div className="flex items-center justify-between border-b border-stone-800 px-3 py-2 text-xs text-stone-400">
            <span>
              {baseIsValidated
                ? 'Changes since the last validated version'
                : 'Unsaved changes — compared to what is saved and running now'}
            </span>
            {!diff.tooLarge && (
              <span className="tabular-nums">
                <span className="text-emerald-300">+{diff.added}</span>{' '}
                <span className="text-rose-300">−{diff.removed}</span>
              </span>
            )}
          </div>
          {diff.tooLarge ? (
            <p className="px-3 py-3 text-xs text-stone-400">
              Too long to diff in the browser. Compare offline before shipping this one.
            </p>
          ) : diff.added === 0 && diff.removed === 0 ? (
            // Not `rows.length === 0` — collapseUnchanged returns a single 'gap' row
            // for an all-identical diff, so that test never fired.
            <p className="px-3 py-3 text-xs text-stone-400">
              Identical to the {baseIsValidated ? 'validated' : 'saved'} version.
            </p>
          ) : (
            <div className="max-h-[420px] overflow-auto py-1">
              {diff.rows.map((row, idx) => (
                <DiffRow key={`${row.type}:${idx}`} row={row} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
